import { useState, useEffect } from "react";
import { useParams, Link } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/public/Navbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageSEO } from "@/hooks/usePageSEO";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, User, Eye, Phone, Building2 } from "lucide-react";
import { getCuratedGuideBySlug } from "@/data/curatedGuides";
import { getBlogCoverImage, DEFAULT_MUMBAI_FALLBACK } from "@/utils/blogImages";
import { trackBlogArticleView } from "@/services/blogViewsService";

interface BlogPostProps {
  initialPost?: any;
  slug?: string;
}

function renderFormattedContent(content: string) {
  if (!content) return null;

  const lines = content.split(/\r?\n/);
  const blocks: Array<{ type: "markdown" | "table"; content?: string; lines?: string[] }> = [];
  let currentText: string[] = [];
  let inTable = false;
  let tableLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isTableLine = line.trim().startsWith("|") && line.trim().endsWith("|");

    if (isTableLine) {
      if (!inTable) {
        if (currentText.length > 0) {
          blocks.push({ type: "markdown", content: currentText.join("\n") });
          currentText = [];
        }
        inTable = true;
      }
      tableLines.push(line.trim());
    } else {
      if (inTable) {
        blocks.push({ type: "table", lines: tableLines });
        tableLines = [];
        inTable = false;
      }
      currentText.push(line);
    }
  }

  if (inTable && tableLines.length > 0) {
    blocks.push({ type: "table", lines: tableLines });
  } else if (currentText.length > 0) {
    blocks.push({ type: "markdown", content: currentText.join("\n") });
  }

  return (
    <div className="space-y-4 text-foreground/90">
      {blocks.map((block, idx) => {
        if (block.type === "table" && block.lines && block.lines.length >= 2) {
          const headerLine = block.lines[0];
          const headers = headerLine
            .split("|")
            .map((c) => c.trim())
            .filter(Boolean);

          const rowLines = block.lines.slice(2);
          const rows = rowLines.map((row) =>
            row
              .split("|")
              .map((c) => c.trim())
              .filter(Boolean)
          );

          return (
            <div key={idx} className="overflow-x-auto my-6 border rounded-xl bg-card shadow-sm">
              <table className="w-full text-sm text-left border-collapse">
                <thead className="bg-muted/70 border-b font-semibold text-foreground">
                  <tr>
                    {headers.map((h, hIdx) => (
                      <th key={hIdx} className="p-3.5 text-xs md:text-sm font-bold tracking-wide">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-muted/30 transition-colors">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="p-3.5 text-xs md:text-sm">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
          <div key={idx} className="space-y-4">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-3xl md:text-4xl font-bold font-playfair italic mt-8 mb-4 text-foreground leading-tight">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl md:text-3xl font-bold font-playfair italic mt-8 mb-4 text-foreground pb-2 border-b border-border/60 leading-snug">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl md:text-2xl font-semibold mt-6 mb-3 text-foreground leading-snug">
                    {children}
                  </h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-lg font-semibold mt-4 mb-2 text-foreground">
                    {children}
                  </h4>
                ),
                p: ({ children }) => (
                  <p className="text-base md:text-lg leading-relaxed text-foreground/90 mb-4">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 space-y-2 mb-5 text-foreground/90 text-base md:text-lg leading-relaxed">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 space-y-2 mb-5 text-foreground/90 text-base md:text-lg leading-relaxed">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed">
                    {children}
                  </li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-foreground">
                    {children}
                  </strong>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary pl-4 italic my-5 text-muted-foreground bg-muted/20 py-2.5 rounded-r">
                    {children}
                  </blockquote>
                ),
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                    {children}
                  </a>
                ),
                hr: () => <hr className="my-8 border-border" />,
              }}
            >
              {block.content || ""}
            </ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
}

export default function BlogPost({ initialPost, slug: propSlug }: BlogPostProps = {}) {
  const routeParams = useParams<{ slug: string }>();
  const slug =
    propSlug ||
    routeParams?.slug ||
    (typeof window !== "undefined"
      ? window.location.pathname.split("/blog/")[1]?.replace(/\/+$/, "")
      : undefined);

  const [liveViews, setLiveViews] = useState<number | null>(null);

  const { data: queryPost, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    initialData: initialPost ?? undefined,
    queryFn: async () => {
      if (!slug) return null;
      const cleanSlug = slug.toLowerCase().trim();

      // 1. Check curated guides first
      const curated = getCuratedGuideBySlug(cleanSlug);
      if (curated) return curated;

      // 2. Query Supabase
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", cleanSlug)
        .eq("is_published", true)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const post = queryPost || initialPost;

  // Only resolved articles can trigger view tracking, never the not-found path.
  useEffect(() => {
    setLiveViews(null);
    if (!slug || !post || post.slug !== slug.toLowerCase().trim()) return;
    let active = true;
    trackBlogArticleView(slug).then((updatedCount) => {
      if (active && typeof updatedCount === "number") {
        setLiveViews(updatedCount);
      }
    });
    return () => { active = false; };
  }, [slug, post?.slug]);

  const isActuallyLoading = !post && isLoading;
  const coverImage = post ? getBlogCoverImage(post) : undefined;
  const displayViews = liveViews ?? post?.views_count;

  usePageSEO({
    title: post ? `${post.meta_title || post.title} | MNRE Blog` : "Loading… | MNRE Blog",
    description: post?.meta_description || post?.excerpt || "Read this article on MNRE Blog.",
    canonicalPath: slug ? `/blog/${slug}` : "/blog",
    ogImage: coverImage,
    ogType: "article",
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.published_at,
      author: { "@type": "Person", name: post.author_name || "MNRE Team" },
      image: coverImage,
    } : undefined,
  });

  if (isActuallyLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-16 max-w-3xl space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you are looking for may have moved or is no longer available.</p>
          <Link to="/blog">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to All Articles
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <article className="container mx-auto px-4 py-10 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>

        {post.category && <Badge variant="secondary" className="mb-3 capitalize">{post.category}</Badge>}
        <h1 className="text-3xl md:text-4xl font-bold font-playfair italic mb-4 leading-tight">{post.title}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
          {post.author_name && <span className="flex items-center gap-1"><User className="h-4 w-4" />{post.author_name}</span>}
          {post.published_at && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {format(new Date(post.published_at), "MMMM d, yyyy")}
            </span>
          )}
          {typeof displayViews === "number" && (
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {displayViews === 1 ? "1 view" : `${displayViews} views`}
            </span>
          )}
        </div>

        {coverImage && (
          <div className="relative w-full rounded-2xl overflow-hidden mb-8 shadow-md max-h-[500px] bg-muted border border-border/50">
            <img
              src={coverImage}
              alt={post.title}
              className="w-full h-full object-cover max-h-[500px]"
              loading="eager"
              decoding="async"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_MUMBAI_FALLBACK;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white text-xs drop-shadow pointer-events-none">
              <span className="bg-black/60 backdrop-blur-md px-3 py-1 rounded-full border border-white/20 font-medium">
                Mumbai Real Estate Insights
              </span>
              {post.category && (
                <span className="bg-black/60 backdrop-blur-md px-3 py-1 rounded-full border border-white/20 font-medium capitalize">
                  {post.category}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Formatted Content with Tables & Direct Answers */}
        <div className="my-6">
          {renderFormattedContent(post.content)}
        </div>

        {/* Structured FAQ Accordion */}
        {post.faqs && Array.isArray(post.faqs) && post.faqs.length > 0 && (
          <section className="mt-12 pt-8 border-t">
            <h2 className="text-2xl font-bold font-playfair italic mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {post.faqs.map((faq: any, idx: number) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="border rounded-xl px-4 bg-card">
                  <AccordionTrigger className="text-left font-semibold text-base py-3.5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Property Discovery & Advisory Call to Action */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-primary/10 via-background to-secondary/10 border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-lg">Looking for Verified Properties in Mumbai?</h3>
            <p className="text-sm text-muted-foreground mt-1">Explore curated listings with verified RERA documentation and current market pricing.</p>
          </div>
          <div className="flex gap-2.5 shrink-0">
            <Link to="/properties">
              <Button variant="outline" size="sm">
                <Building2 className="h-4 w-4 mr-1.5" /> Browse Listings
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="sm">
                <Phone className="h-4 w-4 mr-1.5" /> Contact Expert
              </Button>
            </Link>
          </div>
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t">
            {post.tags.map((tag: string) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
